import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DeviceLockService } from './device-lock.service';
import { DeviceLockHeldError } from './device.utils';
import { ENDPOINTS } from '../../enums/endpoints.enum';

/**
 * Detects a lost device lock mid-session.
 *
 * Every backend feature endpoint sits behind deviceLockMiddleware and answers 423 when the
 * lock is held by another device (e.g. the user force-acquired it elsewhere). This interceptor
 * forwards that signal to DeviceLockService, which updates `lockState$` and opens the lock
 * screen. The device-lock endpoints themselves are excluded: their 423 is part of the regular
 * acquire flow and handled by the service.
 *
 * The 423 is swallowed (not rethrown) once handled: the lock screen is a blocking, non-dismissable
 * modal, so callers' own error handling (e.g. generic "request failed" notifications) must not
 * also fire for this error.
 */
@Injectable()
export class DeviceLockLostInterceptor implements HttpInterceptor {
    private readonly deviceLockService = inject(DeviceLockService);

    public intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        return next.handle(req).pipe(
            catchError((error: unknown) => {
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 423 &&
                    !req.url.includes(ENDPOINTS.DeviceLockGet)
                ) {
                    this.deviceLockService.handleRemoteLockLoss(error.error as DeviceLockHeldError | undefined);
                    return EMPTY;
                }
                return throwError(() => error);
            }),
        );
    }
}
